import { Button, Card, Form, Input, Radio, Select, Space, Tag, Typography } from "antd";
import { SpellCheckTextArea, SpellCheckInput } from "../../../../../../components/spellcheck";
import DatePicker, { TimePicker } from "../../../../../../components/inputs/MomentDatePicker";
import React from "react";
import { AiOutlineClose, AiOutlineFile, AiOutlineUpload } from "react-icons/ai";

const { Option } = Select;
const { Text } = Typography;

const ComposeMessageForm = ({
  form,
  inputRef,
  isEditMode,
  classes = [],
  classId,
  onClassChange,
  classDetails,
  selectedStudents = [],
  selectedParents = [],
  onSelectAll,
  onRemoveParent,
  onStudentMultiChange,
  subject,
  onSubjectChange,
  messageText,
  onMessageChange,
  uploadedFiles = [],
  onRemoveFile,
  onFileSelect,
  sendType,
  onSendTypeChange,
  scheduledDate,
  onScheduledDate,
  recurrenceConfig,
  onRecurrenceChange,
  templates = [],
  selectedTemplate,
  onTemplateChange,
  onSend,
  onResetForm,
  targetingType = "selected",
  onTargetingTypeChange,
  isSending = false,
}) => {
  const isSendDisabled =
    !subject?.trim() ||
    !messageText?.trim() ||
    (!isEditMode &&
      targetingType === "selected" &&
      selectedParents.length === 0);

  return (
    <Form form={form} layout="vertical">
      {/* ── Recipient Targeting (decide audience first) ── */}
      <div style={{ marginBottom: 16 }}>
        <Form.Item label="Targeting" style={{ marginBottom: 8 }}>
          <Radio.Group
            className="announcement-radio-group"
            value={targetingType}
            onChange={(e) => onTargetingTypeChange(e.target.value)}
          >
            <Radio value="selected">By Class/Selection</Radio>
            <Radio value="all">All Parents (Full School)</Radio>
          </Radio.Group>
        </Form.Item>
        {targetingType === "all" && (
          <Tag
            color="orange"
            style={{
              marginBottom: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>📢</span>
            <span>This message will be sent to EVERY parent in the school.</span>
          </Tag>
        )}
      </div>

      {/* ── Class / Student / Parent selection (only relevant for "By Class/Selection") ── */}
      {targetingType === "selected" && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: classId ? "1fr 1fr" : "1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <Form.Item label="Class" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                allowClear
                placeholder="Select Class"
                value={classId}
                onChange={onClassChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {classes.map((data) => (
                  <Option key={data._id} value={data._id}>
                    {data.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {classId && (
              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span>Select Students</span>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: "auto" }}
                      onClick={onSelectAll}
                    >
                      Select All
                    </Button>
                  </div>
                }
                style={{ marginBottom: 0 }}
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Select students to message"
                  value={selectedStudents.map((s) => s._id)}
                  onChange={onStudentMultiChange}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {classDetails?.data?.map((student) => (
                    <Option key={student._id} value={student._id}>
                      {student.first_name} {student.last_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </div>

          {selectedParents.length > 0 && (
            <Form.Item
              label={`Selected Parents (${selectedParents.length})`}
              style={{ marginBottom: 16 }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  padding: 12,
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  background: "#fafafa",
                }}
              >
                {selectedParents.map((parent) => (
                  <Card
                    key={parent._id}
                    size="small"
                    style={{ minWidth: 230, marginBottom: 0, borderRadius: 6 }}
                    bodyStyle={{ padding: "10px 12px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {parent.first_name} {parent.last_name}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ fontSize: 12, marginLeft: 4 }}
                        >
                          ({parent.role})
                        </Text>
                        {parent.student_name && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 11,
                              display: "block",
                              marginTop: 2,
                            }}
                          >
                            Student: {parent.student_name}
                          </Text>
                        )}
                      </div>
                      <AiOutlineClose
                        style={{
                          color: "#ff4d4f",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        size={16}
                        onClick={() => onRemoveParent(parent._id)}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            </Form.Item>
          )}
        </>
      )}

      {/* ── Template & Subject ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Form.Item label="Template (Optional)" style={{ marginBottom: 0 }}>
          <Select
            showSearch
            allowClear
            placeholder="Select a template"
            value={selectedTemplate}
            onChange={(val) => {
              const template = templates.find((t) => t._id === val);
              onTemplateChange(val, template);
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {templates.map((t) => (
              <Option key={t._id} value={t._id}>
                {t.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Subject" required style={{ marginBottom: 0 }}>
          <SpellCheckInput
            placeholder="Enter subject"
            value={subject}
            onChange={onSubjectChange}
            allowClear
          />
        </Form.Item>
      </div>
      {/* ── Message ── */}
      <Form.Item label="Message" required style={{ marginBottom: 16 }}>
        <SpellCheckTextArea
          ref={inputRef}
          rows={5}
          placeholder="Type your message here…"
          value={messageText}
          onChange={onMessageChange}
          showCount
          maxLength={5000}
        />
      </Form.Item>

      {/* ── Attached Files Preview ── */}
      {uploadedFiles?.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {uploadedFiles.map((doc, index) => (
            <Card
              key={index}
              size="small"
              style={{ borderRadius: 6 }}
              bodyStyle={{
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <AiOutlineFile size={14} />
              <Text
                style={{
                  fontSize: 12,
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.name}
              </Text>
              <Button
                type="text"
                size="small"
                icon={<AiOutlineClose size={12} />}
                style={{ padding: 0, width: 18, height: 18 }}
                onClick={() => onRemoveFile(index)}
              />
            </Card>
          ))}
        </div>
      )}

      {/* ── Send Scheduling (decided last, right before sending) ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16,
          alignItems: "flex-end",
        }}
      >
        <Form.Item label="When to Send" style={{ marginBottom: 0 }}>
          <Radio.Group
            className="announcement-radio-group"
            value={sendType}
            onChange={onSendTypeChange}
          >
            <Radio value="now">Send Now</Radio>
            <Radio value="scheduled">Scheduled</Radio>
            <Radio value="recurring">Recurring</Radio>
          </Radio.Group>
        </Form.Item>

        {sendType === "scheduled" && (
          <Form.Item label="Date &amp; Time" style={{ marginBottom: 0 }}>
            <DatePicker
              showTime
              placeholder="Select Date &amp; Time"
              onChange={onScheduledDate}
              value={scheduledDate}
              style={{ width: 220 }}
            />
          </Form.Item>
        )}

        {sendType === "recurring" && (
          <>
            <Form.Item label="Interval" style={{ marginBottom: 0 }}>
              <Select
                value={recurrenceConfig?.type}
                onChange={(val) => onRecurrenceChange({ type: val })}
                style={{ width: 130 }}
              >
                <Option value="daily">Daily</Option>
                <Option value="weekdays">Weekdays</Option>
                <Option value="weekly">Weekly</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Time" style={{ marginBottom: 0 }}>
              <TimePicker
                format="HH:mm"
                placeholder="Select Time"
                onChange={(val) =>
                  onRecurrenceChange({
                    at_time: val ? val.format("HH:mm") : "09:00",
                  })
                }
              />
            </Form.Item>
            <Form.Item label="Timezone" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder="Select Timezone"
                value={recurrenceConfig?.timezone}
                onChange={(val) => onRecurrenceChange({ timezone: val })}
                style={{ width: 180 }}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                <Option value="Asia/Kolkata">Asia/Kolkata (IST)</Option>
                <Option value="UTC">UTC</Option>
                <Option value="America/New_York">America/New_York (EST)</Option>
                <Option value="America/Chicago">America/Chicago (CST)</Option>
                <Option value="America/Denver">America/Denver (MST)</Option>
                <Option value="America/Los_Angeles">
                  America/Los_Angeles (PST)
                </Option>
                <Option value="Europe/London">Europe/London (GMT/BST)</Option>
                <Option value="Europe/Paris">Europe/Paris (CET)</Option>
                <Option value="Asia/Dubai">Asia/Dubai (GST)</Option>
                <Option value="Asia/Singapore">Asia/Singapore (SGT)</Option>
                <Option value="Australia/Sydney">Australia/Sydney (AEST)</Option>
              </Select>
            </Form.Item>
          </>
        )}
      </div>

      {/* ── Footer: Upload + Send ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "1px solid #f0f0f0",
          paddingTop: 16,
          marginTop: 4,
        }}
      >
        <Space>
          <>
            <Button
              icon={<AiOutlineUpload size={18} />}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() =>
                document.getElementById("composeFileInput").click()
              }
              title="Attach files"
            >
              Attach
            </Button>
            <input
              id="composeFileInput"
              type="file"
              style={{ display: "none" }}
              multiple
              accept=".pdf,.jpeg,.jpg,.png,.mp3,.mp4,.wav,.webm"
              onChange={onFileSelect}
            />
          </>
          {isEditMode && <Button onClick={onResetForm}>Cancel Edit</Button>}
        </Space>

        <Button
          type="primary"
          size="large"
          onClick={onSend}
          disabled={isSendDisabled || isSending}
          loading={isSending}
        >
          {isEditMode ? "Update Message" : "Send Message"}
        </Button>
      </div>
    </Form>
  );
};

export default ComposeMessageForm;
